import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SOURCES } from '../data/sources';
import { isMarketingSourceId } from '../data/marketingTypes';
import type { ConnectedSourceId } from '../types';
import { Icon, Ring } from './shared/Icon';
import { ContextLine } from './context/ContextLine';

const SRC_ICON: Record<string, string> = {
  reservations: 'calendar',
  tableplan: 'table',
  pos: 'euro',
  ga: 'web',
  googleads: 'euro',
  gsc: 'web',
  reviews: 'star',
  crm: 'users',
  weather: 'cloud',
  events: 'ticket',
  demandgen: 'web',
  opentable: 'table',
};

export function IntegrationsDrawer({
  connected,
  serverConnected,
  focusSourceId,
  onClose,
  onToggle,
  onManageMarketing,
  onOpenMarketingAttribution,
  onOpenReviews,
  onRequestPosConnect,
  onOpenGoogleAccounts,
}: {
  connected: ConnectedSourceId[];
  /** Raw list from the server before user opt-out preferences. */
  serverConnected?: ConnectedSourceId[];
  /** Scroll to and highlight this source when the drawer opens. */
  focusSourceId?: ConnectedSourceId | null;
  onClose: () => void;
  onToggle?: (id: ConnectedSourceId) => void;
  onManageMarketing?: (id: ConnectedSourceId) => void;
  /** Opens the marketing attribution drill (when a Google source is on). */
  onOpenMarketingAttribution?: () => void;
  /** Opens the guest reviews drill (when reviews source is on). */
  onOpenReviews?: () => void;
  /** Opens the POS value explainer when this scope has no basket data. */
  onRequestPosConnect?: () => void;
  /** Opens "Your Google accounts", outside any restaurant's context. */
  onOpenGoogleAccounts?: () => void;
}) {
  const { t } = useTranslation('growth');
  const { t: tSettings } = useTranslation('settings');
  const bodyRef = useRef<HTMLDivElement>(null);
  const offered = serverConnected ?? connected;
  const n = connected.length;

  const handleToggle = (id: ConnectedSourceId, core?: boolean, alwaysOn?: boolean) => {
    if (core || alwaysOn || !onToggle) {
      return;
    }
    onToggle(id);
  };

  useEffect(() => {
    if (!focusSourceId || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(
      `[data-source-id="${focusSourceId}"]`,
    ) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('focused');
    const timer = window.setTimeout(() => el.classList.remove('focused'), 2200);
    return () => window.clearTimeout(timer);
  }, [focusSourceId]);

  return (
    <>
      <div className="scrim" onClick={onClose} role="presentation" />
      <div className="drawer" data-ftue="integrations-drawer">
        <div className="dr-head">
          <div>
            <h2>{tSettings('drawer.title')}</h2>
            <p>
              {tSettings('drawer.lead')}
            </p>
          </div>
          <button
            type="button"
            className="ghost-btn"
            onClick={onClose}
            aria-label={tSettings('drawer.close')}
          >
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="dr-body" ref={bodyRef}>
          {/* Which venues these sources belong to; the period and filters don't change them. */}
          <ContextLine variant="sources" />
          {onOpenGoogleAccounts ? (
            <button type="button" className="src-card clickable acx-accounts-link" onClick={onOpenGoogleAccounts}>
              <div className="src-ic">
                <Icon name="link" size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <span className="src-name">{t('accountConnect.openAccounts')}</span>
                <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('accountConnect.openAccountsHint')}</div>
              </div>
              <Icon name="chevronRight" size={15} />
            </button>
          ) : null}
          <div className="dr-stat">
            <Ring
              pct={(n / SOURCES.length) * 100}
              size={46}
              color="var(--st-steady)"
            >
              {n}
            </Ring>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {tSettings('drawer.connected', { n, total: SOURCES.length })}
              </div>
              <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>
                {tSettings('drawer.layers')}
              </div>
            </div>
          </div>
          {SOURCES.map(s => {
            const on = connected.includes(s.id);
            const offeredByServer = offered.includes(s.id);
            const isPos = s.id === 'pos';
            const lockedOn = !!(s.core || s.alwaysOn);
            const toggleable = !lockedOn && !!onToggle;
            return (
              <div
                key={s.id}
                data-source-id={s.id}
                className={`src-card${on ? ' on' : ''}${toggleable ? ' clickable' : ''}`}
                style={{
                  cursor: lockedOn
                    ? 'default'
                    : toggleable
                      ? 'pointer'
                      : 'default',
                }}
                role={toggleable ? 'button' : undefined}
                tabIndex={toggleable ? 0 : undefined}
                onClick={
                  toggleable
                    ? () => handleToggle(s.id, s.core, s.alwaysOn)
                    : undefined
                }
                onKeyDown={
                  toggleable
                    ? e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggle(s.id, s.core, s.alwaysOn);
                        }
                      }
                    : undefined
                }
              >
                <div className="src-ic">
                  <Icon name={SRC_ICON[s.id] ?? 'database'} size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <span className="src-name">{s.name}</span>
                    {s.core && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'var(--petrol-ink)',
                          textTransform: 'uppercase',
                          letterSpacing: '.05em',
                        }}
                      >
                        {tSettings('drawer.core')}
                      </span>
                    )}
                    {isPos && offeredByServer && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: 'var(--st-steady)',
                          textTransform: 'uppercase',
                          letterSpacing: '.05em',
                        }}
                      >
                        {tSettings('drawer.live')}
                      </span>
                    )}
                  </div>
                  <div className="src-kind">{s.kind}</div>
                  <div className="src-desc">{s.desc}</div>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--ink-3)',
                      marginTop: 4,
                    }}
                  >
                    {tSettings('drawer.unlocks', { list: s.unlocks.join(', ') })}
                  </div>
                  {isPos && offeredByServer && (
                    <div
                      style={{
                        fontSize: 13,
                        color: 'var(--ink-3)',
                        marginTop: 4,
                      }}
                    >
                      {tSettings('drawer.posAuto')}
                    </div>
                  )}
                  {isPos && !offeredByServer && (
                    <button
                      type="button"
                      className="src-manage"
                      onClick={e => {
                        e.stopPropagation();
                        onRequestPosConnect?.();
                      }}
                    >
                      {t('cta.whyPos')}
                    </button>
                  )}
                  {isMarketingSourceId(s.id) && onManageMarketing && (
                    <button
                      type="button"
                      className="src-manage"
                      onClick={e => {
                        e.stopPropagation();
                        onManageMarketing(s.id);
                      }}
                    >
                      {offeredByServer ? t('cta.manageGoogle') : t('cta.connectGoogle')}
                    </button>
                  )}
                  {isMarketingSourceId(s.id) &&
                    offeredByServer &&
                    on &&
                    onOpenMarketingAttribution && (
                      <button
                        type="button"
                        className="src-manage"
                        onClick={e => {
                          e.stopPropagation();
                          onOpenMarketingAttribution();
                        }}
                      >
                        {t('cta.openGrowth')}
                      </button>
                    )}
                  {s.id === 'reviews' &&
                    offeredByServer &&
                    on &&
                    onOpenReviews && (
                      <button
                        type="button"
                        className="src-manage"
                        onClick={e => {
                          e.stopPropagation();
                          onOpenReviews();
                        }}
                      >
                        {t('cta.viewReviews')}
                      </button>
                    )}
                </div>
                {!isPos && (
                  <div
                    className={`toggle${on ? ' on' : ''}${s.core ? ' core' : ''}${toggleable && !offeredByServer ? ' muted' : ''}`}
                    aria-hidden="true"
                  />
                )}
              </div>
            );
          })}
          <div className="dr-note">
            {tSettings('drawer.marketplace')}
          </div>
        </div>
      </div>
    </>
  );
}
